import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Typography,
  Box,
  useTheme,
} from '@mui/material';

import CloseIcon from '../icons/CloseIcon';

interface PopupAction {
  label: string;
  onClick: () => void;
  variant?: 'primary' | 'secondary' | 'danger';
  disabled?: boolean;
  loading?: boolean;
}

interface PopupProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  message?: string;
  actions?: PopupAction[];
  showCloseButton?: boolean;
  children?: React.ReactNode;
}

const Popup: React.FC<PopupProps> = ({
  isOpen,
  onClose,
  title,
  message,
  actions,
  showCloseButton = true,
  children
}) => {
  const theme = useTheme();

  const getButtonVariant = (variant?: string) => {
    switch (variant) {
      case 'primary':
        return 'contained';
      case 'danger':
        return 'contained';
      case 'secondary':
      default:
        return 'outlined';
    }
  };

  const getButtonColor = (variant?: string) => {
    switch (variant) {
      case 'primary':
        return 'primary';
      case 'danger':
        return 'error';
      case 'secondary':
      default:
        return 'inherit';
    }
  };

  const handleKeyDown = React.useCallback((e: KeyboardEvent) => {
    if (e.key === "Enter") {
      const mainAction = actions?.find(
        (action) => action.variant === "primary" || action.variant === "danger",
      );
      if (mainAction) {
        mainAction.onClick();
      }
    }
  }, []);

  React.useEffect(() => {
    if (!isOpen) return;

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, handleKeyDown]);

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography
          component="span"
          sx={{
            fontSize: "19px",
            fontWeight: 600,
            color: theme.palette.text.primary,
          }}
        >
          {title}
        </Typography>
        {showCloseButton && (
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: theme.palette.text.secondary,
              '&:hover': {
                color: theme.palette.text.primary,
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', '& svg': { width: 20, height: 20 } }}>
              <CloseIcon className="" />
            </Box>
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2 }}>
        <Box sx={{ paddingTop: 2 }}>
          {children ? (
            children
          ) : (
            message && (
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  lineHeight: 1.75,
                }}
              >
                {message}
              </Typography>
            )
          )}
        </Box>
      </DialogContent>

      {actions && actions.length > 0 && (
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            gap: 1.5,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          {actions.map((action, index) => (
            <Button
              key={index}
              onClick={action.onClick}
              disabled={action.disabled}
              loading={action.loading}
              variant={getButtonVariant(action.variant)}
              color={getButtonColor(action.variant)}
              sx={{
                minWidth: 100,
                ...(action.variant === 'danger' && {
                  backgroundColor: theme.palette.error.main,
                  color: theme.palette.error.contrastText,
                  '&:hover': {
                    backgroundColor: theme.palette.error.dark,
                  },
                }),
                ...(action.variant === 'primary' && {
                  backgroundColor: theme.palette.info.main,
                  color: '#fff',
                  '&:hover': {
                    backgroundColor: theme.palette.info.dark,
                  },
                }),
                ...(action.variant === 'secondary' && {
                  backgroundColor: theme.palette.action.hover,
                  color: theme.palette.text.primary,
                  borderColor: theme.palette.divider,
                  '&:hover': {
                    backgroundColor: theme.palette.action.selected,
                    borderColor: theme.palette.divider,
                  },
                }),
              }}
            >
              {action.label}
            </Button>
          ))}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default Popup;
